import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiX, FiRefreshCw, FiTruck, FiMessageSquare, FiInbox,
  FiAlertTriangle, FiZap, FiShoppingCart, FiHeadphones, FiKey, FiClock,
} from 'react-icons/fi';
import request from '../../utils/request';
import NotificationSection from './NotificationSection';

const POLL_INTERVAL = 30_000;
const LAST_SEEN_KEY = 'bellhopt_notif_sidebar_last_seen';
const DEFAULT_ASSIGNABLE_IDS = [
  '693a611bee1166397a0e4ec1',
  '693a611cee1166397a0e4ec2',
];
const DEFAULT_ASSIGNABLE_USERS = DEFAULT_ASSIGNABLE_IDS.map((id, i) => ({
  id,
  label: `Admin ${i + 1}`,
}));

export default function NotificationSidebar({
  isOpen,
  onClose,
  onNavigateToOrder,
  onNavigateToInquiry,
  onNavigateToServiceRequest,
  assignableUsers,
}) {
  const [fetchedUsers, setFetchedUsers] = useState(null);
  const resolvedUsers = assignableUsers || fetchedUsers || DEFAULT_ASSIGNABLE_USERS;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastSeenAt, setLastSeenAt] = useState(() => {
    try { return localStorage.getItem(LAST_SEEN_KEY) || null; } catch { return null; }
  });
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await request.get('/hosts');
        const idSet = new Set(DEFAULT_ASSIGNABLE_IDS);
        const users = (res.data || [])
          .filter((u) => idSet.has(String(u._id || u.id)))
          .map((u) => ({
            id: String(u._id || u.id),
            label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Admin',
          }));
        if (!cancelled && users.length) setFetchedUsers(users);
      } catch {
        // keep defaults
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await request.get('/tickets/notifications');
      if (res.data?.success) {
        setData(res.data);
      }
    } catch (err) {
      console.error('[NotificationSidebar] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
      pollRef.current = setInterval(fetchNotifications, POLL_INTERVAL);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [isOpen, fetchNotifications]);

  useEffect(() => {
    if (isOpen) {
      const ts = new Date().toISOString();
      setLastSeenAt(ts);
      try { localStorage.setItem(LAST_SEEN_KEY, ts); } catch {}
    }
  }, [isOpen]);

  const handleAssign = useCallback(async (item, userId) => {
    try {
      const ticketId = item.ticketId || item._id;
      if (!ticketId) return;
      const user = resolvedUsers.find(u => u.id === userId);
      await request.patch(`/tickets/${ticketId}/reassign`, {
        assignedTo: userId,
        assignedToName: user?.label || 'Admin',
        reason: 'Assigned from notification sidebar',
      });
      fetchNotifications();
    } catch (err) {
      console.error('[NotificationSidebar] assign error:', err);
    }
  }, [fetchNotifications, resolvedUsers]);

  const handleCloseItem = useCallback(async (item) => {
    try {
      const ticketId = item.ticketId || item._id;
      if (!ticketId) return;
      await request.patch(`/tickets/${ticketId}/complete`, {});
      fetchNotifications();
    } catch (err) {
      console.error('[NotificationSidebar] close error:', err);
    }
  }, [fetchNotifications]);

  const handleOpenItem = useCallback((item) => {
    if (item.uniqueId || item.orderId) {
      onNavigateToOrder?.(item.orderId || item._id);
    } else if (item.email && item.message) {
      onNavigateToInquiry?.(item._id);
    } else if (item.servicesRequested) {
      onNavigateToServiceRequest?.(item._id);
    }
  }, [onNavigateToOrder, onNavigateToInquiry, onNavigateToServiceRequest]);

  const driverStallItems = data?.driverStallTickets || [];
  const driverlessItems = data?.driverlessOrders || [];
  const inquiryItems = data?.openInquiries || [];
  const srItems = data?.unansweredSRs || [];
  const noStoreItems = data?.noStoreOrder || [];
  const missingEntryItems = data?.missingEntryInstructions || [];
  const missingDeliveryItems = data?.missingDeliveryTime || [];
  const staleEscalatedItems = data?.staleEscalated || [];
  const driverSupportItems = data?.unansweredDriverSupport || [];

  const totalCount =
    driverStallItems.length + driverlessItems.length + inquiryItems.length +
    srItems.length + noStoreItems.length + missingEntryItems.length +
    missingDeliveryItems.length + staleEscalatedItems.length +
    driverSupportItems.length;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.aside
          initial={{ x: '100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '100%', opacity: 0 }}
          transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="
            fixed top-0 right-0 z-40 h-full w-[380px] max-w-[90vw]
            bg-white/95 dark:bg-gray-900/95
            backdrop-blur-xl backdrop-saturate-150
            border-l border-gray-200/60 dark:border-gray-700/40
            shadow-[-8px_0_30px_rgba(0,0,0,0.06)]
            flex flex-col
            overflow-hidden
          "
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800/60">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900 dark:text-white tracking-tight">
                Ops Alerts
              </span>
              {totalCount > 0 && (
                <span className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1.5">
                  {totalCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={fetchNotifications}
                disabled={loading}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="Refresh"
              >
                <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin">
            {!data && loading ? (
              <div className="flex items-center justify-center py-12">
                <FiRefreshCw className="w-5 h-5 text-gray-300 dark:text-gray-600 animate-spin" />
              </div>
            ) : (
              <>
                {/* Critical: Driver stall alerts (always on top, always open) */}
                {driverStallItems.length > 0 && (
                  <NotificationSection
                    title="Driver Alert"
                    icon={FiAlertTriangle}
                    items={driverStallItems}
                    type="driverStall"
                    accentColor="red"
                    defaultOpen={true}
                    lastSeenAt={lastSeenAt}
                    onAssign={handleAssign}
                    onCloseItem={handleCloseItem}
                    onOpenItem={handleOpenItem}
                    assignableUsers={resolvedUsers}
                  />
                )}

                <NotificationSection
                  title="No Driver Assigned"
                  icon={FiTruck}
                  items={driverlessItems}
                  type="driverless"
                  accentColor="coral"
                  defaultOpen={true}
                  lastSeenAt={lastSeenAt}
                  onAssign={handleAssign}
                  onCloseItem={handleCloseItem}
                  onOpenItem={handleOpenItem}
                  assignableUsers={resolvedUsers}
                />

                <NotificationSection
                  title="Open Inquiries"
                  icon={FiMessageSquare}
                  items={inquiryItems}
                  type="inquiry"
                  accentColor="amber"
                  defaultOpen={true}
                  lastSeenAt={lastSeenAt}
                  onAssign={handleAssign}
                  onCloseItem={handleCloseItem}
                  onOpenItem={handleOpenItem}
                  assignableUsers={resolvedUsers}
                />

                <NotificationSection
                  title="Unanswered Requests"
                  icon={FiInbox}
                  items={srItems}
                  type="serviceRequest"
                  accentColor="blue"
                  defaultOpen={true}
                  lastSeenAt={lastSeenAt}
                  onAssign={handleAssign}
                  onCloseItem={handleCloseItem}
                  onOpenItem={handleOpenItem}
                  assignableUsers={resolvedUsers}
                />

                <NotificationSection
                  title="No Store Order"
                  icon={FiShoppingCart}
                  items={noStoreItems}
                  type="noStoreOrder"
                  accentColor="violet"
                  defaultOpen={false}
                  lastSeenAt={lastSeenAt}
                  onAssign={handleAssign}
                  onCloseItem={handleCloseItem}
                  onOpenItem={handleOpenItem}
                  assignableUsers={resolvedUsers}
                />

                <NotificationSection
                  title="Missing Entry Instructions"
                  icon={FiKey}
                  items={missingEntryItems}
                  type="missingEntry"
                  accentColor="amber"
                  defaultOpen={false}
                  lastSeenAt={lastSeenAt}
                  onAssign={handleAssign}
                  onCloseItem={handleCloseItem}
                  onOpenItem={handleOpenItem}
                  assignableUsers={resolvedUsers}
                />

                <NotificationSection
                  title="Missing Delivery Time"
                  icon={FiClock}
                  items={missingDeliveryItems}
                  type="missingDelivery"
                  accentColor="emerald"
                  defaultOpen={false}
                  lastSeenAt={lastSeenAt}
                  onAssign={handleAssign}
                  onCloseItem={handleCloseItem}
                  onOpenItem={handleOpenItem}
                  assignableUsers={resolvedUsers}
                />

                <NotificationSection
                  title="Escalated — No Action"
                  icon={FiZap}
                  items={staleEscalatedItems}
                  type="staleEscalated"
                  accentColor="red"
                  defaultOpen={false}
                  lastSeenAt={lastSeenAt}
                  onAssign={handleAssign}
                  onCloseItem={handleCloseItem}
                  onOpenItem={handleOpenItem}
                  assignableUsers={resolvedUsers}
                />

                <NotificationSection
                  title="Driver Support Waiting"
                  icon={FiHeadphones}
                  items={driverSupportItems}
                  type="driverSupport"
                  accentColor="amber"
                  defaultOpen={false}
                  lastSeenAt={lastSeenAt}
                  onAssign={handleAssign}
                  onCloseItem={handleCloseItem}
                  onOpenItem={handleOpenItem}
                  assignableUsers={resolvedUsers}
                />

                {totalCount === 0 && !loading && (
                  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <div className="w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mb-3">
                      <FiCheck className="w-5 h-5 text-emerald-500" />
                    </div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      All clear
                    </span>
                    <span className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                      No urgent items right now
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-800/60 text-[10px] text-gray-400 dark:text-gray-500">
            Auto-refreshes every 30s
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

function FiCheck(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
